// /src/components/admin/AdminPanel.jsx
// Admin Panel - Tabbed interface for platform administration
// Access: explicit users.is_sys_admin grant only
// Tabs: see the `tabs` array below. (Demo Management retired in Phase B3 — demos live in the `demo` schema.)

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import StandardButton from '../core/StandardButton';
import ErrorReportModal from '../core/ErrorReportModal';
import StatsTab from './StatsTab';
import UserManagementTab from './UserManagementTab';
import ContentModerationTab from './ContentModerationTab';
import SocialImagesTab from './SocialImagesTab';
import ReputationTuningTab from './ReputationTuningTab';
import RoadmapTab from './RoadmapTab';
import LocationDepthAdmin from './LocationDepthAdmin';
import DesignSystemTab from './DesignSystemTab';
import LocalityAdminsTab from './LocalityAdminsTab';
import { isPlatformAdmin } from '../../utils/adminHelpers';

const AdminPanel = ({ currentUser, onBack }) => {
  const theme = useTheme();
  const { t } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState('stats');
  const [showErrorReport, setShowErrorReport] = useState(false);

  // Logo click listener - navigate back to MainFeed
  useEffect(() => {
    const handleLogoClick = () => {
      if (onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Access guard - platform superadmin only
  if (!isPlatformAdmin(currentUser)) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: theme?.bg || '#F5F5F5'
      }}>
        <HeaderContainer>
          <HeaderButtonsForm
            currentContext={{ name: t('adminPanel') }}
            onBack={onBack}
          />
        </HeaderContainer>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          color: theme?.text?.primary || '#1A1A1A',
          marginTop: '80px'
        }}>
          {t('accessDenied')}
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'stats', label: t('tabPlatformStats') },
    { id: 'users', label: t('tabUserManagement') },
    { id: 'content', label: t('tabContentModeration') },
    { id: 'images', label: t('tabSocialImages') },
    { id: 'reputation', label: t('tabReputationTuning') },
    { id: 'location', label: t('tabLocationDepth', 'Location Depth') },
    { id: 'roadmap', label: t('tabRoadmap') },
    { id: 'stewards', label: t('tabStewards') },
    { id: 'design', label: 'Design System' }
  ];

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: theme?.bg || '#F5F5F5'
    },
    tabBar: {
      display: 'flex',
      borderBottom: `2px solid ${theme?.border || '#DDD'}`,
      backgroundColor: 'white',
      padding: '0 16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      position: 'fixed',
      top: '80px',
      left: 0,
      right: 0,
      zIndex: 10,
      // Nine nowrap tabs overflow a phone; scroll them sideways so later tabs stay reachable.
      overflowX: 'auto'
    },
    tab: (isActive) => ({
      padding: '14px 24px',
      border: 'none',
      background: 'none',
      borderBottom: isActive ? `3px solid ${theme?.primary || '#024B99'}` : '3px solid transparent',
      color: isActive ? (theme?.primary || '#024B99') : (theme?.text?.secondary || '#666'),
      fontWeight: isActive ? 'bold' : '500',
      fontSize: '15px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginBottom: '-2px',
      whiteSpace: 'nowrap'
    }),
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      paddingBottom: '80px',
      marginTop: '140px'
    },
    reportButtonWrap: {
      position: 'fixed',
      bottom: '20px',
      right: '40px',
      zIndex: 100
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'stats':
        return <StatsTab currentUser={currentUser} />;
      case 'users':
        return <UserManagementTab currentUser={currentUser} />;
      case 'content':
        return <ContentModerationTab currentUser={currentUser} />;
      case 'images':
        return <SocialImagesTab currentUser={currentUser} />;
      case 'reputation':
        return <ReputationTuningTab currentUser={currentUser} />;
      case 'location':
        return <LocationDepthAdmin currentUser={currentUser} />;
      case 'roadmap':
        return <RoadmapTab currentUser={currentUser} />;
      case 'stewards':
        return <LocalityAdminsTab currentUser={currentUser} />;
      case 'design':
        return <DesignSystemTab />;
      default:
        return <div>{t('selectTab')}</div>;
    }
  };

  return (
    <div style={styles.container}>
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={{ name: t('adminPanel') }}
        onBack={onBack}
      />

      <div style={styles.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={styles.tab(activeTab === tab.id)}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = theme?.primary || '#024B99';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = theme?.text?.secondary || '#666';
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.content}>
        {renderTabContent()}
      </div>

      {/* Fixed Report Problems Button */}
      <div style={styles.reportButtonWrap}>
        <StandardButton
          variant="icon"
          text="?"
          color="#F9A61B"
          title={t('reportProblem')}
          onClick={() => setShowErrorReport(true)}
        />
      </div>

      {/* Error Report Modal */}
      <ErrorReportModal
        isOpen={showErrorReport}
        onClose={() => setShowErrorReport(false)}
        currentView="admin"
      />
    </div>
  );
};

export default AdminPanel;
